import { Component, EventEmitter, Input, OnInit, Output, OnChanges, ChangeDetectorRef} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SelectItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { IGeo } from 'src/app/models/igeo.interface';
import { IKeyValue, sortIKeyValueByName } from 'src/app/models/ikeyvalue.interface';
import { PersonOnlineRegForm } from 'src/app/models/iperson.interface';
import { GeocodeService } from 'src/app/services/data/geocode.service';
import { RankService } from 'src/app/services/data/rank.service';
import { SpecialityService } from 'src/app/services/data/speciality.service';
import { ENUM_INCOMING_CHANNELS, GLOBAL_VARIABLES } from '../../data/const.data';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';

@UntilDestroy()
@Component({
  selector: 'app-person-reg-online-form',
  templateUrl: './person-reg-online-form.component.html',
  styleUrls: ['./person-reg-online-form.component.css']
})
export class PersonRegOnlineFormComponent implements OnInit, OnChanges {

  @Input() formData: PersonOnlineRegForm | undefined;
  @Input() isAdminEdit: boolean = false;
  @Input() buttonCaption: string = 'Зарегистрироваться';
  @Output() onSubmitted = new EventEmitter<PersonOnlineRegForm>();

  isFormReady = false;
  filteredGeo$: Observable<IGeo[]>;

  suggestions: IGeo[] = [];

  frm: UntypedFormGroup;
  data: PersonOnlineRegForm;

  specialitiesPlaceholder = '';

  lstSex: SelectItem[] = [];
  lstScienceDegree: SelectItem[] = [];
  lstScienceStatus: SelectItem[] = [];
  lstSpecialities: SelectItem[] = [];
  lstRanks: SelectItem[] = [];
  lstChannels: SelectItem[] = ENUM_INCOMING_CHANNELS.ALL.map(o => ({ label: o, value: o} as SelectItem))

  fltScienceDegree: SelectItem[] = [];
  fltScienceStatus: SelectItem[] = [];

  isMobile: boolean = false;

  calendarSettings = GLOBAL_VARIABLES.CALENDAR_RU;

  get f() { return this.frm?.controls; }

  constructor(
    private fb: UntypedFormBuilder,
    private srvSp: SpecialityService,
    private geo: GeocodeService,
    private deviceService: DeviceDetectorService,
    private srvRk: RankService,
    private cdr: ChangeDetectorRef
  ) {
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnChanges() {
    if (this.formData) {
    this.initForm(true);
    } else {
      this.initForm(false);
    }

  }
  ngOnInit() {
    this.srvSp.getAll().toPromise()
    .then(data =>
      this.lstSpecialities = data.filter(o => o.id > 0).slice().sort(sortIKeyValueByName).map((o: IKeyValue) => ({ label: o.name, value: o.name } as SelectItem))
      );
    if (this.isAdminEdit) {
      this.srvRk.getAll().toPromise().then(data => {
        this.lstRanks = data.sort(sortIKeyValueByName).map((o: IKeyValue) => ({ label: o.name, value: o.name } as SelectItem))
      });
    }
    this.initForm();
  }

  patchValueToForm(val: PersonOnlineRegForm, isAgree: boolean) {
    let obj = {
      firstName: val.firstName,
      secondName: val.secondName,
      thirdName: val.thirdName,
      phone: val.phone,
      email: val.email,
      workplace: val.workplace,
      position: val.position,
      specialities: val.specialities ?? [],
      filterGeo: '',
      city: val.city,
      region: val.region,
      country: val.country,
      isAgreeSendPersonalData: isAgree,
      rank: val.rank,
      incomingChannel: val.incomingChannel
    };
    this.frm.patchValue(obj);
    this.specialitiesPlaceholder = 'Выберите специальности';
    this.cdr.markForCheck();
  }

  initForm(isAgree: boolean = false) {
    this.frm = this.fb.group({
      firstName: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
      secondName: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
      thirdName: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      email: new UntypedFormControl('', { validators: [Validators.required, Validators.email, Validators.pattern(GLOBAL_VARIABLES.REGEXP_EMAIL)] }),
      workplace: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
      position: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
      specialities: new UntypedFormControl([], { validators: [Validators.required, Validators.minLength(1), Validators.maxLength(4)] }),
      filterGeo: new UntypedFormControl(),
      city: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
      region: new UntypedFormControl(''),
      country: new UntypedFormControl(''),
      rank: new UntypedFormControl(''),
      incomingChannel: new UntypedFormControl(''),
      isAgreeSendPersonalData: new UntypedFormControl(isAgree, { validators: Validators.requiredTrue , updateOn: 'change'  })
    });

    this.filteredGeo$ = this.frm.controls['filterGeo'].valueChanges.pipe(
      untilDestroyed(this),
      filter(text => text.length > 3),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(text => this.geo.search(text)),
    );

    if (this.formData) {
      this.patchValueToForm(this.formData, isAgree);
    }
    if (!this.isAdminEdit) {
      this.frm.controls['rank'].patchValue('Участник');
    }
    if (this.isAdminEdit) {
      this.frm.controls['isAgreeSendPersonalData'].patchValue(true);
    }
    this.isFormReady = true;
  }

  filterScienceDegree(event) {
    let query = event.query;
    this.fltScienceDegree = [];
    for (let i = 0; i < this.lstScienceDegree.length; i++) {
      let country = this.lstScienceDegree[i];
      if (country.label.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        this.fltScienceDegree.push(country);
      }
    }
  }

  search(e: any) {
    this.geo.search(e.query).pipe(
      untilDestroyed(this),
      tap(data => this.suggestions = data)
    ).subscribe()
  }

  onSelectGeo(event: AutoCompleteSelectEvent) {
    const obj: IGeo = event.value;  // Get the selected object from the event

    // Update the form with the relevant address fields
    this.frm.patchValue({
      city: obj.address.city || obj.address.town,
      region: obj.address.state,
      country: obj.address.country
    }, { emitEvent: false });
  }

  onCancel() {
  }

  onSubmit(f: UntypedFormGroup) {
    f.markAllAsTouched();
    if (!f.valid) {
      console.log(f)
      return;
    }

    const data = {
      firstName: f.value['firstName'] || '',
      secondName: f.value['secondName'] || '',
      thirdName: f.value['thirdName'] || '',

      phone: f.value['phone'] || '',
      email: f.value['email'] || '',

      workplace: f.value['workplace'] || '',
      position: f.value['position'] || '',

      specialities: f.value['specialities'] || [],

      city: f.value['city'] || '',
      region: f.value['region'] || '',
      country: f.value['country'] || '',
      rank: f.value['rank'] || 'Участник',
      incomingChannel: f.value['incomingChannel'] ?? ENUM_INCOMING_CHANNELS.DEFAULT
    } as PersonOnlineRegForm;

    this.onSubmitted.emit(data);
  }


}
